import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useBilingual } from '../hooks/useBilingual';
import { useLanguage } from '../contexts/LanguageContext';
import './ProfilePage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { bilingualMode, secondaryLanguage, toggleBilingual, updateSecondaryLanguage, tb } = useBilingual();
  const { currentLanguage, changeLanguage, languages, getCurrentLanguageInfo } = useLanguage();
  const [previewLang, setPreviewLang] = useState(secondaryLanguage);
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedPrimaryLang, setSelectedPrimaryLang] = useState(currentLanguage);
  const [selectedSecondaryLang, setSelectedSecondaryLang] = useState(secondaryLanguage);
  const [langSaveSuccess, setLangSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 프로필 정보
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    grade: ''
  });

  // 비밀번호 변경
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // 로그인 확인
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    if (!token || userType !== 'student') {
      navigate('/login');
      return;
    }

    // 현재 사용자 정보 로드
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setProfileData({
      name: user.name || '',
      email: user.email || '',
      grade: user.grade || ''
    });
  }, [navigate]);

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (profileData.name) formData.append('name', profileData.name);
      if (profileData.email) formData.append('email', profileData.email);
      if (profileData.grade) formData.append('grade', profileData.grade);

      const response = await axios.put(
        `${API_BASE_URL}/api/auth/profile`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // 로컬 스토리지 업데이트
      localStorage.setItem('user', JSON.stringify(response.data));
      setSuccess('프로필이 성공적으로 업데이트되었습니다.');
    } catch (error) {
      setError(error.response?.data?.detail || '프로필 업데이트에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // 비밀번호 확인
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('새 비밀번호는 최소 6자 이상이어야 합니다.');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('current_password', passwordData.currentPassword);
      formData.append('new_password', passwordData.newPassword);

      await axios.put(
        `${API_BASE_URL}/api/auth/change-password`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccess('비밀번호가 성공적으로 변경되었습니다.');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      setError(error.response?.data?.detail || '비밀번호 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 미리보기용 번역 텍스트 (resource bundle에서 직접 조회 — 렌더 중 changeLanguage 호출 없이)
  const getSecondaryText = (key) => {
    const resources = i18n.getResourceBundle(previewLang, 'translation');
    if (!resources) return '';
    return key.split('.').reduce((obj, k) => obj?.[k], resources) || '';
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>{tb('profile.title')}</h1>
        <p>{tb('profile.subtitle')}</p>
      </div>

      {/* 탭 메뉴 */}
      <div className="tab-container">
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            👤 {tb('profile.tab.basicInfo')}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            🔒 {tb('profile.tab.password')}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'language' ? 'active' : ''}`}
            onClick={() => setActiveTab('language')}
          >
            🌐 {tb('profile.tab.language')}
          </button>
        </div>

        {/* 알림 메시지 */}
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span>
            {success}
          </div>
        )}

        {/* 기본 정보 탭 */}
        {activeTab === 'profile' && (
          <div className="card">
            <h3>{tb('profile.basicInfo.title')}</h3>
            <form onSubmit={updateProfile}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">{tb('profile.basicInfo.name')}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-input"
                  value={profileData.name}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">{tb('profile.basicInfo.email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              {/* <div className="form-group">
                <label htmlFor="grade" className="form-label">학습 단계</label>
                <select
                  id="grade"
                  name="grade"
                  className="form-input"
                  value={profileData.grade}
                  onChange={handleProfileChange}
                  required
                >
                  <option value="">선택</option>
                  <option value="elementary">Elementary (초등)</option>
                  <option value="youth">Youth (중고등)</option>
                  <option value="select100">Select 100 (수능 필독서)</option>
                  <option value="general">General (일반)</option>
                </select>
              </div> */}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? tb('profile.basicInfo.updating') : tb('profile.basicInfo.submit')}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  {tb('profile.basicInfo.back')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 비밀번호 변경 탭 */}
        {activeTab === 'password' && (
          <div className="card">
            <h3>{tb('profile.password.title')}</h3>
            <form onSubmit={changePassword}>
              <div className="form-group">
                <label htmlFor="currentPassword" className="form-label">{tb('profile.password.current')}</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  className="form-input"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword" className="form-label">{tb('profile.password.new')}</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  className="form-input"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
                <small className="form-help">{tb('profile.password.minLength')}</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">{tb('profile.password.confirm')}</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className="form-input"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength="6"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? tb('profile.password.changing') : tb('profile.password.submit')}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                >
                  {tb('profile.password.reset')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 언어 설정 탭 */}
        {activeTab === 'language' && (
          <div className="card lang-settings-card">

            {/* 주 언어 섹션 */}
            <div className="lang-setup-section">
              <div className="lang-setup-header">
                <div className="lang-setup-title">
                  {tb('profile.language.primaryLabel')}
                  <span className="lang-setup-title-en"> (First Language)</span>
                </div>
                <div className="lang-setup-desc">{tb('profile.language.primaryDesc')}</div>
              </div>
              <div className="lang-btn-grid">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    type="button"
                    className={`lang-btn ${selectedPrimaryLang === lang.code ? 'lang-btn--active' : ''}`}
                    onClick={() => {
                      setSelectedPrimaryLang(lang.code);
                      if (selectedSecondaryLang === lang.code) {
                        const fallback = languages.find(l => l.code !== lang.code);
                        if (fallback) setSelectedSecondaryLang(fallback.code);
                      }
                    }}
                  >
                    <span className="lang-btn-code">{lang.flag.replace(/\uD83C[\uDDE6-\uDDFF]/g, m => m)}</span>
                    <span className="lang-btn-name">{lang.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="lang-divider" />

            {/* 바이랭귀지 섹션 */}
            <div className="lang-setup-section">
              <div className="lang-setup-header">
                <div className="lang-setup-title-row">
                  <div>
                    <div className="lang-setup-title">
                      {tb('profile.language.bilingual.title')}
                      <span className="lang-setup-title-en"> (Bilingual)</span>
                    </div>
                    <div className="lang-setup-desc">{tb('profile.language.bilingual.desc')}</div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={bilingualMode}
                      onChange={toggleBilingual}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
              <div className="lang-btn-grid">
                {languages
                  .filter(lang => lang.code !== selectedPrimaryLang)
                  .map(lang => (
                    <button
                      key={lang.code}
                      type="button"
                      className={`lang-btn ${selectedSecondaryLang === lang.code ? 'lang-btn--active' : ''}`}
                      onClick={() => {
                        setSelectedSecondaryLang(lang.code);
                        setPreviewLang(lang.code);
                      }}
                    >
                      <span className="lang-btn-code">{lang.flag}</span>
                      <span className="lang-btn-name">{lang.name}</span>
                    </button>
                  ))}
              </div>
            </div>

            <div className="lang-divider" />

            {/* 저장 버튼 */}
            <div className="lang-actions">
              <button
                type="button"
                className="btn btn-secondary lang-btn-back"
                onClick={() => setActiveTab('profile')}
              >
                ‹ {tb('profile.basicInfo.back')}
              </button>
              <button
                type="button"
                className="btn btn-primary lang-btn-save"
                onClick={() => {
                  if (selectedPrimaryLang !== currentLanguage) {
                    changeLanguage(selectedPrimaryLang);
                  }
                  updateSecondaryLanguage(selectedSecondaryLang);
                  setLangSaveSuccess(true);
                  setTimeout(() => setLangSaveSuccess(false), 2500);
                }}
              >
                {langSaveSuccess ? '✅ 저장됨' : tb('profile.language.saveSettings')}
              </button>
            </div>
          </div>
        )}

        {/* 빠른 메뉴 */}
        <div className="card">
          <h3>{tb('profile.quickMenu.title')}</h3>
          <div className="quick-menu-grid">
            <button 
              className="quick-menu-item"
              onClick={() => navigate('/game-progress')}
            >
              <span className="quick-menu-icon">🎮</span>
              <span className="quick-menu-label">{tb('profile.quickMenu.progress')}</span>
            </button>
            <button 
              className="quick-menu-item"
              onClick={() => navigate('/test')}
            >
              <span className="quick-menu-icon">🎯</span>
              <span className="quick-menu-label">{tb('profile.quickMenu.play')}</span>
            </button>
            <button 
              className="quick-menu-item"
              onClick={() => navigate('/books')}
            >
              <span className="quick-menu-icon">📚</span>
              <span className="quick-menu-label">{tb('profile.quickMenu.books')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 